import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { Card, type CardProps, Box, Flex, Text } from '@mantine/core'
import addMinutes from 'date-fns/addMinutes'
import isAfter from 'date-fns/isAfter'
import isSameDay from 'date-fns/isSameDay'
import { useEffect, useState, useMemo } from 'react'

import {
  useQueryGetAllBookingInsurances,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useQueryGetUserBookingsSettings } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type ParameterID } from '@flecto/types'
import { DateTimePickerInput, Icon } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'
import { isValidParameterID } from '@flecto/utils'

import { useBookingLoadingState } from '../../../booking-details'
import { useModalBookingDatesExtensionConfirmation } from '../../hooks/useModalBookingDatesExtensionConfirmation'
import { useUpdateBookingDates } from '../../hooks/useUpdateBookingDates'

export type CardBookingDatesSelectionProps = Omit<CardProps, 'children'> & {
  bookingId?: ParameterID
}

export function CardBookingDatesSelection({
  bookingId,
}: CardBookingDatesSelectionProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_dates' })

  const [isFirstRender, setIsFirstRender] = useState(true)

  const [newStartAt, setNewStartAt] = useState<string | undefined>(undefined)

  const [newEndAt, setNewEndAt] = useState<string | undefined>(undefined)

  const [localStartAt, setLocalStartAt] = useState<string | undefined>(
    undefined
  )
  const [localEndAt, setLocalEndAt] = useState<string | undefined>(undefined)

  const { isMobile } = useMediaQuery()

  const [validationError, setValidationError] = useState('')

  const { isUpdating, setIsUpdating } = useBookingLoadingState()

  const { data: booking } = useQueryGetBookingById({ bookingId })

  const { data: userBookingsSettings } = useQueryGetUserBookingsSettings()

  const { data: bookingInsurances } = useQueryGetAllBookingInsurances({
    bookingId,
  })

  const { openModal: openModalExtensionConfirmation } =
    useModalBookingDatesExtensionConfirmation({
      bookingId,
      newDates: { start_at: newStartAt, end_at: newEndAt },
      insurances: bookingInsurances!,
      booking: booking!,
    })

  const {
    handleUpdateBookingDates,
    handleCreateBookingWithDates,
    isLoading: isSaving,
  } = useUpdateBookingDates({ bookingId })

  const [defaultStartAtHours, setDefaultStartAtHours] = useState<string | null>(
    null
  )
  const [defaultEndAtHours, setDefaultEndAtHours] = useState<string | null>(
    null
  )

  const isBookingUpdating = isUpdating || isSaving

  const isBookingCompleted = useMemo(
    () => booking?.status === 'completed',
    [booking]
  )

  const isBookingCanceled = useMemo(
    () => booking?.status === 'canceled',
    [booking]
  )

  const isBookingOngoing = useMemo(
    () => booking?.status === 'ongoing',
    [booking]
  )

  useEffect(() => {
    if (userBookingsSettings) {
      const { default_booking_start_at, default_booking_end_at } =
        userBookingsSettings

      const startAt = new Date(
        default_booking_start_at ?? '2000-01-01T09:00:00'
      )
      const endAt = new Date(default_booking_end_at ?? '2000-01-01T18:00:00')

      if (startAt) {
        const startAtHours = String(startAt.getHours()).padStart(2, '0')
        const startAtMinutes = String(startAt.getMinutes()).padStart(2, '0')

        setDefaultStartAtHours(`${startAtHours}:${startAtMinutes}`)
      }

      if (endAt) {
        const endAtHours = String(endAt.getHours()).padStart(2, '0')
        const endAtMinutes = String(endAt.getMinutes()).padStart(2, '0')

        setDefaultEndAtHours(`${endAtHours}:${endAtMinutes}`)
      }
    }
  }, [userBookingsSettings])

  const minDateEnd = useMemo(() => {
    let minDateValue: Date | undefined

    try {
      minDateValue = new Date(localStartAt ?? '')
      // 15 minutes after start date
      minDateValue = addMinutes(minDateValue, 15)

      return minDateValue
    } catch {
      return minDateValue
    }
  }, [localStartAt])

  const isStartDisabled =
    isBookingCompleted || isBookingCanceled || isBookingOngoing

  const isEndDisabled = !booking
    ? !localStartAt
    : !localStartAt || isBookingCompleted || isBookingCanceled

  const getMinTime = (startAt: string): string | undefined => {
    if (!startAt) {
      return undefined
    }

    const startDate = addMinutes(Date.parse(startAt), 15)

    const hours = String(startDate.getHours()).padStart(2, '0')
    const minutes = String(startDate.getMinutes()).padStart(2, '0')

    return `${hours}:${minutes}`
  }

  const handleSaveDates = async (start?: string, end?: string) => {
    setValidationError('')
    setLocalStartAt(start)
    setLocalEndAt(end)

    if (!start || !end) {
      return
    }

    if (new Date(start).getTime() > new Date(end).getTime()) {
      setValidationError(t('error_start_date_ahead'))
      return
    }

    if (!isValidParameterID(bookingId) && start && end) {
      setIsUpdating(true)

      await handleCreateBookingWithDates({
        start_at: start,
        end_at: end,
      })

      setIsUpdating(false)
      return
    }

    if (!isFirstRender) {
      setNewStartAt(start)
      setNewEndAt(end)
      return
    }

    handleUpdateBookingDates({
      start_at: isStartDisabled ? undefined : start,
      end_at: end,
    })
  }

  useEffect(() => {
    if (newEndAt || newStartAt) {
      openModalExtensionConfirmation()
    }
  }, [newEndAt, newStartAt])

  useEffect(() => {
    if (booking) {
      setLocalStartAt(booking?.start_at)
      setLocalEndAt(booking?.end_at)
    }
  }, [booking])

  useEffect(() => {
    if (booking?.end_at || booking?.start_at) {
      setIsFirstRender(false)
    }
  }, [booking?.end_at, booking?.start_at])

  return (
    <Card withBorder>
      <Text fw={500}>{t('label_dates')}</Text>
      <Text c='dimmed' size='sm'>
        {t('card_dates_selection_description')}
      </Text>
      <Flex
        gap='md'
        align='flex-start'
        mt='md'
        wrap={isMobile ? 'wrap' : undefined}
        justify='stretch'
      >
        <DateTimePickerInput
          fullWidth
          value={localStartAt}
          isLoading={isBookingUpdating}
          disabled={isStartDisabled || booking?.meta?.has_issued_insurances}
          error={validationError}
          size={isMobile ? 'sm' : 'md'}
          label={isMobile ? t('label_pickup') : undefined}
          defaultTimeValue={defaultStartAtHours}
          onChange={(date) => {
            handleSaveDates(date, localEndAt)
          }}
        />
        {!isMobile && (
          <Box mt='sm'>
            <Icon icon={faArrowRight} color='gray.7' />
          </Box>
        )}
        <DateTimePickerInput
          fullWidth
          value={localEndAt}
          isLoading={isSaving}
          disabled={isEndDisabled}
          size={isMobile ? 'sm' : 'md'}
          label={isMobile ? t('label_return') : undefined}
          defaultTimeValue={defaultEndAtHours}
          excludeDate={(date: Date) => {
            if (!minDateEnd) {
              return false
            }
            const allow =
              isAfter(date, minDateEnd) || isSameDay(date, minDateEnd)

            return !allow
          }}
          onChange={(date) => {
            handleSaveDates(localStartAt, date)
          }}
          minTime={
            localStartAt && localEndAt
              ? isSameDay(new Date(localStartAt), new Date(localEndAt))
                ? getMinTime(localStartAt)
                : undefined
              : undefined
          }
        />
      </Flex>
    </Card>
  )
}
